import React, { useState } from 'react';
import Footer from '../Header/Footer';
import Header from '../Header/Header';
import Config from '../Header/Config';
import { FaSearch } from 'react-icons/fa';

export default function IMEI_transactions() {
  const [imei, setImei] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const apiUrl = Config.apiUrl;

  const fetchIMEIData = async () => {
    if (!imei || imei.trim() === '') {
      alert('Please enter IMEI number');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await fetch(`${apiUrl}/imei-transactions/${imei.trim()}`);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch IMEI transactions';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `Error ${response.status}: ${response.statusText || errorMessage}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.status === 'success') {
        setData(result);
      } else {
        setError(result.message || 'Failed to fetch IMEI transactions');
      }
    } catch (error) {
      console.error('Error fetching IMEI data:', error);
      setError(error.message || 'Network Error: Unable to connect to server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchIMEIData();
    }
  };

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <h4 className="fw-bold py-3 mb-4">IMEI Transactions</h4>

                {/* IMEI Input Section */}
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 heading-heighlight">Search IMEI</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8 mb-3">
                        <label className="form-label" htmlFor="imei-input">
                          IMEI Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="imei-input"
                          className="form-control"
                          placeholder="Enter IMEI Number"
                          value={imei}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setImei(value);
                          }}
                          onKeyPress={handleKeyPress}
                          style={{ textTransform: 'uppercase' }}
                          disabled={loading}
                        />
                      </div>
                      <div className="col-md-4 mb-3 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={fetchIMEIData}
                          disabled={loading || !imei.trim()}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Loading...
                            </>
                          ) : (
                            <>
                              <FaSearch className="me-2" />
                              Search
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <strong>Error:</strong> {error}
                  </div>
                )}

                {/* Data Display Section */}
                {data && (
                  <>
                    {/* IMEI Info */}
                    <div className="card mb-4">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 heading-heighlight">IMEI Information</h5>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                              <div className="mb-3">
                                <label className="text-muted small mb-1 d-block">IMEI Number</label>
                                <div className="fw-bold" style={{ fontSize: '18px', color: '#696cff' }}>{data.imei}</div>
                              </div>
                              <div>
                                <label className="text-muted small mb-1 d-block">Status</label>
                                <span className="badge bg-label-success">{data.status?.toUpperCase() || 'SUCCESS'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                              <div className="row g-2">
                                <div className="col-6">
                                  <label className="text-muted small mb-1 d-block">Purchase</label>
                                  <div className="fw-semibold" style={{ fontSize: '16px' }}>{data.purchase_entries?.length || 0}</div>
                                </div>
                                <div className="col-6">
                                  <label className="text-muted small mb-1 d-block">Sales</label>
                                  <div className="fw-semibold" style={{ fontSize: '16px' }}>{data.sales_entries?.length || 0}</div>
                                </div>
                                <div className="col-6">
                                  <label className="text-muted small mb-1 d-block">Buyback</label>
                                  <div className="fw-semibold" style={{ fontSize: '16px' }}>{data.buyback_entries?.length || 0}</div>
                                </div>
                                <div className="col-6">
                                  <label className="text-muted small mb-1 d-block">Total</label>
                                  <div className="fw-semibold" style={{ fontSize: '16px' }}>{data.statement?.length || 0}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Purchase Entries */}
                    {data.purchase_entries && data.purchase_entries.length > 0 && (
                      <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 heading-heighlight">Purchase Entries</h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive text-nowrap">
                            <table className="table">
                              <thead>
                                <tr className="text-nowrap">
                                  <th>SN</th>
                                  <th>Invoice No</th>
                                  <th>Date</th>
                                  <th>Model Name</th>
                                  <th>Color</th>
                                  <th>Purchase Price</th>
                                  <th>Sale Price</th>
                                  <th>Created At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.purchase_entries.map((entry, index) => (
                                  <tr key={entry.id}>
                                    <td>{index + 1}</td>
                                    <td>{entry.invoice_no}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.model_name}</td>
                                    <td>{entry.color}</td>
                                    <td>₹{parseFloat(entry.purchase_price || 0).toLocaleString('en-IN')}</td>
                                    <td>₹{parseFloat(entry.sale_price || 0).toLocaleString('en-IN')}</td>
                                    <td>{entry.created_at}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sales Entries */}
                    {data.sales_entries && data.sales_entries.length > 0 && (
                      <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 heading-heighlight">Sales Entries</h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive text-nowrap">
                            <table className="table">
                              <thead>
                                <tr className="text-nowrap">
                                  <th>SN</th>
                                  <th>Invoice No</th>
                                  <th>Date</th>
                                  <th>Model Name</th>
                                  <th>Color</th>
                                  <th>Sold Price</th>
                                  <th>Created At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.sales_entries.map((entry, index) => (
                                  <tr key={entry.id}>
                                    <td>{index + 1}</td>
                                    <td>{entry.invoice_no}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.model_name}</td>
                                    <td>{entry.color}</td>
                                    <td>₹{parseFloat(entry.sold_price || 0).toLocaleString('en-IN')}</td>
                                    <td>{entry.created_at}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Buyback Entries */}
                    {data.buyback_entries && data.buyback_entries.length > 0 && (
                      <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 heading-heighlight">Buyback Entries</h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive text-nowrap">
                            <table className="table">
                              <thead>
                                <tr className="text-nowrap">
                                  <th>SN</th>
                                  <th>Invoice No</th>
                                  <th>Date</th>
                                  <th>Model Name</th>
                                  <th>Color</th>
                                  <th>Buyback Price</th>
                                  <th>Created At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.buyback_entries.map((entry, index) => (
                                  <tr key={entry.id}>
                                    <td>{index + 1}</td>
                                    <td>{entry.invoice_no || 'N/A'}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.model_name}</td>
                                    <td>{entry.color}</td>
                                    <td>₹{parseFloat(entry.buyback_price || 0).toLocaleString('en-IN')}</td>
                                    <td>{entry.created_at}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Statement */}
                    {data.statement && data.statement.length > 0 && (
                      <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 heading-heighlight">Transaction Statement</h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive text-nowrap">
                            <table className="table">
                              <thead>
                                <tr className="text-nowrap">
                                  <th>SN</th>
                                  <th>Entry Type</th>
                                  <th>Invoice No</th>
                                  <th>Entry Date</th>
                                  <th>Amount</th>
                                  <th>Sold Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.statement.map((entry, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <span className={`badge ${
                                        entry.entry_type === 'PURCHASE ENTRY' 
                                          ? 'bg-label-primary' 
                                          : entry.entry_type === 'SALES ENTRY'
                                          ? 'bg-label-success'
                                          : 'bg-label-info'
                                      }`}>
                                        {entry.entry_type}
                                      </span>
                                    </td>
                                    <td>{entry.invoice_no}</td>
                                    <td>{entry.entry_date}</td>
                                    <td>
                                      {entry.amount 
                                        ? `₹${parseFloat(entry.amount).toLocaleString('en-IN')}` 
                                        : '-'}
                                    </td>
                                    <td>
                                      {entry.sold_price 
                                        ? `₹${parseFloat(entry.sold_price).toLocaleString('en-IN')}` 
                                        : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* No Data Message */}
                    {(!data.purchase_entries || data.purchase_entries.length === 0) &&
                     (!data.sales_entries || data.sales_entries.length === 0) &&
                     (!data.buyback_entries || data.buyback_entries.length === 0) && (
                      <div className="alert alert-info" role="alert">
                        No transaction records found for this IMEI.
                      </div>
                    )}
                  </>
                )}
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
